import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { SEARCE_COLORS } from '../../theme';
import SectionTitle from './SectionTitle';
import type { Milestone } from './executiveSummaryTypes';
import {
  getMilestoneRingNodeStyle,
  getMilestoneStatusPill,
  isMilestoneOverdue,
  milestoneStatusFilterOptions,
  phasesFromMilestones,
} from './milestoneUtils';

type MsFilter = 'all' | string;

interface RingTimelineProps {
  milestones: Milestone[];
}

const RingTimeline: React.FC<RingTimelineProps> = ({ milestones }) => {
  const { phases, fillTo } = useMemo(() => phasesFromMilestones(milestones), [milestones]);
  const n = phases.length;
  if (n === 0) return null;

  const halfStepPct = 100 / n / 2;
  const segmentPct = 100 - 2 * halfStepPct;
  const fillPct = (fillTo / n) * segmentPct;

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth: Math.max(n * 72, 320),
        width: '100%',
        p: '6px 4px 10px',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 26,
          height: 3,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${halfStepPct}%`,
            right: `${halfStepPct}%`,
            top: 0,
            height: '100%',
            borderRadius: '3px',
            background: '#e0e0e0',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: `${halfStepPct}%`,
            top: 0,
            height: '100%',
            borderRadius: '3px',
            background: `linear-gradient(90deg, ${SEARCE_COLORS.success}, #34d399)`,
            width: `${fillPct}%`,
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          width: '100%',
          gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
          columnGap: '4px',
          pt: '10px',
        }}
      >
        {phases.map((phase, idx) => {
          const m = milestones[idx];
          const node = getMilestoneRingNodeStyle(m?.status ?? '', m?.plannedDate ?? '');
          return (
            <Box
              key={`ring-phase-${idx}`}
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6875rem',
                  fontWeight: 800,
                  background: node.bg,
                  border: `2px solid ${node.border}`,
                  color: node.color,
                }}
              >
                {idx + 1}
              </Box>
              <Typography
                component="p"
                title={milestones[idx]?.milestone}
                sx={{
                  mt: '6px',
                  mb: 0,
                  width: '100%',
                  minWidth: 0,
                  px: '1px',
                  fontSize: '0.5rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  lineHeight: 1.25,
                  color: node.labelColor,
                  textTransform: 'uppercase',
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {phase.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

interface MilestonesSectionProps {
  milestones: Milestone[];
  /** First fetch for milestones API — show spinner instead of placeholder rows */
  loading?: boolean;
}

const MilestonesSection: React.FC<MilestonesSectionProps> = ({ milestones, loading }) => {
  const [filter, setFilter] = useState<MsFilter>('all');

  const filterOptions = useMemo(() => milestoneStatusFilterOptions(milestones), [milestones]);

  const visible = useMemo(() => {
    if (filter === 'all') {
      return milestones.map((m, idx) => ({ m, originalIdx: idx }));
    }
    return milestones
      .map((m, idx) => ({ m, originalIdx: idx }))
      .filter((row) => row.m.status === filter);
  }, [milestones, filter]);

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        border: `1px solid ${SEARCE_COLORS.border}`,
        borderRadius: '10px',
        p: '20px 24px',
        boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 0 0 1px rgba(0,0,0,.03)',
        mb: '20px',
      }}
    >
      <Box sx={{ display: 'block' }}>
        <SectionTitle>Milestones &amp; Delivery</SectionTitle>
      </Box>
      <Typography
        sx={{
          fontSize: '0.75rem',
          color: SEARCE_COLORS.neutral_500,
          mt: '-4px',
          mb: '12px',
          lineHeight: 1.5,
        }}
      >
        <Box component="strong" sx={{ fontWeight: 700 }}>
          Overdue
        </Box>{' '}
        milestones past their planned date use a red edge tint.
      </Typography>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 6,
            minHeight: 220,
          }}
        >
          <CircularProgress size={40} sx={{ color: SEARCE_COLORS.primary }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Loading milestones…
          </Typography>
        </Box>
      ) : (
        <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: '12px' }}>
        {filterOptions.map((opt) => {
          const active = filter === opt.id;
          return (
            <Box
              component="button"
              type="button"
              key={opt.id}
              onClick={() => setFilter(opt.id)}
              sx={{
                background: active ? SEARCE_COLORS.primary : '#fff',
                color: active ? '#fff' : SEARCE_COLORS.textSecondary,
                border: `1px solid ${active ? SEARCE_COLORS.primary : SEARCE_COLORS.border}`,
                p: '6px 14px',
                borderRadius: '999px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              {opt.label}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          overflowX: 'auto',
          borderRadius: '10px',
          border: `1px solid ${SEARCE_COLORS.border}`,
          background: '#fff',
        }}
      >
        <Box
          component="table"
          sx={{
            width: '100%',
            minWidth: 680,
            borderCollapse: 'collapse',
          }}
        >
          <Box
            component="thead"
            sx={{
              '& th': {
                background: `linear-gradient(135deg, ${SEARCE_COLORS.primary} 0%, ${SEARCE_COLORS.primary_light} 100%)`,
                color: '#fff',
                fontSize: '0.6875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
                p: '10px 13px',
                textAlign: 'left',
                borderBottom: 'none',
              },
            }}
          >
            <tr>
              <th>Milestone</th>
              <th>Status</th>
              <th>Planned Date</th>
              <th>Target Shift</th>
              <th>Billable</th>
            </tr>
          </Box>
          <Box
            component="tbody"
            sx={{
              '& td': {
                borderBottom: '1px solid #f1f5f9',
                p: '10px 13px',
                textAlign: 'left',
                fontSize: '0.78rem',
              },
              '& tr.overdue': {
                background:
                  'linear-gradient(90deg, rgba(211,47,47,.12) 0%, rgba(255,235,238,.65) 8%, #fffaf5 100%)',
                boxShadow: `inset 4px 0 0 0 ${SEARCE_COLORS.error}`,
                '& td': { borderBottom: '1px solid #ffcdd2' },
              },
              '& tr:not(.overdue):nth-of-type(even) td': {
                background: '#fafbfc',
              },
            }}
          >
            {visible.length === 0 ? (
              <tr>
                <Box
                  component="td"
                  colSpan={5}
                  sx={{
                    textAlign: 'center',
                    p: '28px',
                    color: SEARCE_COLORS.neutral_500,
                    fontSize: '0.78rem',
                  }}
                >
                  No rows for this filter.
                </Box>
              </tr>
            ) : (
              visible.map(({ m, originalIdx }) => {
                const isOverdue = isMilestoneOverdue(m.status, m.plannedDate);
                const pill = getMilestoneStatusPill(m.status);
                return (
                  <tr key={originalIdx} className={isOverdue ? 'overdue' : undefined}>
                    <td>{m.milestone}</td>
                    <td>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          px: '10px',
                          py: '3px',
                          borderRadius: '999px',
                          fontSize: '0.6875rem',
                          fontWeight: pill.weight,
                          background: pill.bg,
                          color: pill.color,
                        }}
                      >
                        {pill.label}
                      </Box>
                    </td>
                    <td>{m.plannedDate}</td>
                    <td>{m.targetShift}</td>
                    <td>{m.billable}</td>
                  </tr>
                );
              })
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: '18px',
          p: '14px',
          borderRadius: '10px',
          background: '#f8fafc',
          border: '1px solid #f1f5f9',
          overflowX: 'auto',
        }}
      >
        <RingTimeline milestones={milestones} />
      </Box>
        </>
      )}
    </Paper>
  );
};

export default MilestonesSection;
